import { useEffect, useState } from "react";

/**
 * 液态玻璃折射滤镜（SVG feDisplacementMap）。
 * 用 SDF 在 canvas 上算出圆角矩形的位移场：中心不动、边缘把背景向外"挤"，
 * 编码为 R/G 通道后交给 feDisplacementMap，形成真实边缘折射。
 * 仅 Chromium 支持 backdrop-filter: url()，其余浏览器自动退化为模糊玻璃。
 */

function smoothStep(a: number, b: number, t: number) {
  t = Math.max(0, Math.min(1, (t - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** 生成圆角矩形的折射位移图（dataURI PNG） */
function buildDisplacementMap(w: number, h: number, radius: number, strength: number) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(w, h);
  const cx = w / 2;
  const cy = h / 2;
  const halfW = w / 2;
  const halfH = h / 2;
  const r = Math.min(radius, halfW, halfH);
  const band = Math.min(14, halfW * 0.5, halfH * 0.5); // 折射影响带宽（px）

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // 圆角矩形 SDF
      const qx = Math.abs(x - cx) - (halfW - r);
      const qy = Math.abs(y - cy) - (halfH - r);
      const ax = Math.max(qx, 0);
      const ay = Math.max(qy, 0);
      const outside = Math.hypot(ax, ay);
      const inside = Math.min(Math.max(qx, qy), 0);
      const d = outside + inside - r; // <0 在内部

      let dx = 0;
      let dy = 0;
      if (d < 0 && d > -band) {
        // 仅边缘带内折射；越靠边越强，smoothStep 让它自然过渡
        const t = smoothStep(0, 1, -d / band); // 0(边) -> 1(带内缘)
        const k = (1 - t) * strength;
        // 朝外法线（近似）
        const nx = x - cx === 0 ? 0 : (x - cx) / Math.max(1, Math.hypot(x - cx, y - cy));
        const ny = y - cy === 0 ? 0 : (y - cy) / Math.max(1, Math.hypot(x - cx, y - cy));
        dx = -nx * k;
        dy = -ny * k;
      }
      const i = (y * w + x) * 4;
      img.data[i] = 128 + dx; // R -> x 位移
      img.data[i + 1] = 128 + dy; // G -> y 位移
      img.data[i + 2] = 128;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL("image/png");
}

let cached: { key: string; uri: string } | null = null;

export default function LiquidGlassFilter() {
  const [uri, setUri] = useState<string>("");

  useEffect(() => {
    // 延后一帧生成位移图，避免阻塞 React 首次提交；不支持时自然退化为普通玻璃。
    const frame = requestAnimationFrame(() => {
      const userAgent = navigator.userAgent;
      const isChromium = /Chromium|Chrome\/|Edg\//.test(userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
      if (!isChromium || isSafari) return;

      const key = "160x56-r28-s9";
      if (cached?.key === key) {
        setUri(cached.uri);
        return;
      }
      try {
        const map = buildDisplacementMap(160, 56, 28, 9);
        cached = { key, uri: map };
        setUri(map);
      } catch {
        // Canvas 或滤镜不可用时保留普通 backdrop blur。
      }
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  if (!uri) return null;

  return (
    <svg aria-hidden width="0" height="0" style={{ position: "absolute" }}>
      <filter id="liquid-glass-filter" x="-20%" y="-20%" width="140%" height="140%">
        <feImage href={uri} preserveAspectRatio="none" result="map" />
        <feDisplacementMap
          in="SourceGraphic"
          in2="map"
          scale="1"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </svg>
  );
}
